import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderPlus, Library as LibraryIcon, Pencil, Plus, Search, Star, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDuration, formatFileSize } from "@/lib/format";
import {
  type FolderFilter,
  type Library,
  type LibraryFolder,
  type LibraryItem,
  filterLibrary,
  libraryFileUrl,
  useCreateLibraryItem,
  useDeleteLibraryFolder,
  useDeleteLibraryItem,
  useLibrary,
  useSaveLibraryFolder,
  useUpdateLibraryItem,
} from "@/lib/library";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SimpleSelect } from "@/components/ui/select";
import { cn } from "@/lib/cn";
import { KIND_ICONS } from "./libraryKinds";

const NO_FOLDER = "none";
const ICON_BUTTON =
  "shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
const FIELD =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export interface ContentLibraryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Take a material into the reply being written — given when the library is
   * opened from a conversation. Without it the library is only managed.
   */
  onPick?: (item: LibraryItem) => void;
  /** Open straight on this material's form ("Edit" in the templates menu). */
  editId?: string | null;
}

/**
 * The content library: the company's ready-made texts and files, in folders,
 * with a search. Opened from a conversation it is where an answer is taken
 * from — a click on a material puts it into the reply (nothing is sent by the
 * click); everywhere it is where materials are added, changed and removed.
 */
export function ContentLibrary({ open, onOpenChange, onPick, editId = null }: ContentLibraryProps) {
  const { t } = useTranslation();
  const library = useLibrary(open);
  const [folder, setFolder] = useState<FolderFilter>("all");
  const [query, setQuery] = useState("");
  // The material whose form is open: one of the library's, or a new one.
  const [form, setForm] = useState<LibraryItem | "new" | null>(null);

  // Every opening starts from the list — or from the material asked for.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setForm(null);
  }, [open]);
  const asked = editId && open ? library.data?.items.find((i) => i.id === editId) : undefined;
  useEffect(() => {
    if (asked) setForm(asked);
    // Only when the request arrives — not again on every refetch of the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked?.id]);

  const data = library.data;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="h-[min(44rem,90vh)]">
        <DialogHeader>
          <DialogTitle>{t("inbox.library.title")}</DialogTitle>
          <DialogDescription>
            {t(onPick ? "inbox.library.subtitle_pick" : "inbox.library.subtitle")}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <DialogBody>
            <p className="text-sm text-fg-muted">
              {t(library.isError ? "inbox.library.failed" : "inbox.loading")}
            </p>
          </DialogBody>
        ) : form ? (
          <ItemForm
            key={form === "new" ? "new" : form.id}
            item={form === "new" ? null : form}
            library={data}
            folder={folder}
            onDone={() => setForm(null)}
          />
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
              <Folders library={data} folder={folder} onChoose={setFolder} />
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-2 border-b border-border p-3">
                  <div className="relative min-w-0 flex-1">
                    <Search
                      size={15}
                      strokeWidth={2}
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
                    />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("inbox.library.search") ?? ""}
                      aria-label={t("inbox.library.search")}
                      className="h-9 pl-9"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setForm("new")}
                    iconLeft={<Plus size={15} strokeWidth={2.5} aria-hidden="true" />}
                  >
                    {t("inbox.library.add")}
                  </Button>
                </div>
                <Items
                  items={filterLibrary(data.items, folder, query)}
                  empty={data.items.length === 0}
                  onPick={onPick}
                  onEdit={setForm}
                />
              </div>
            </div>
            <DialogFooter className="justify-between py-3 text-xs text-fg-muted">
              <span>
                {t("inbox.library.usage", {
                  used: formatFileSize(data.used_bytes),
                  quota: formatFileSize(data.quota_bytes),
                })}
              </span>
              <span>{t("inbox.library.spintax_hint")}</span>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
ContentLibrary.displayName = "ContentLibrary";

/** The button that opens the library, with the library behind it. */
export function ContentLibraryButton({
  onPick,
  className,
  label,
}: {
  onPick?: (item: LibraryItem) => void;
  className?: string;
  /** Show the name beside the icon (the page header, from `sm` up); without it — the icon alone. */
  label?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("inbox.library.title")}
        title={t("inbox.library.open")}
        className={className}
      >
        <LibraryIcon size={18} strokeWidth={2} aria-hidden="true" />
        {/* On a phone the icon alone: the header's row is short. */}
        {label && <span className="hidden sm:inline">{t("inbox.library.title")}</span>}
      </button>
      <ContentLibrary
        open={open}
        onOpenChange={setOpen}
        onPick={
          onPick &&
          ((item) => {
            onPick(item);
            setOpen(false);
          })
        }
      />
    </>
  );
}
ContentLibraryButton.displayName = "ContentLibraryButton";

/* ------------------------------------------------------------------ folders */

function Folders({
  library,
  folder,
  onChoose,
}: {
  library: Library;
  folder: FolderFilter;
  onChoose: (folder: FolderFilter) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveLibraryFolder();
  const remove = useDeleteLibraryFolder();
  // The folder being named: a new one, or one being renamed.
  const [naming, setNaming] = useState<LibraryFolder | "new" | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const count = (id: string | null) => library.items.filter((i) => i.folder_id === id).length;
  const startNaming = (target: LibraryFolder | "new") => {
    setNaming(target);
    setName(target === "new" ? "" : target.name);
    setError(null);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!naming || !name.trim()) {
      setNaming(null);
      return;
    }
    try {
      const saved = await save.mutateAsync({
        id: naming === "new" ? undefined : naming.id,
        name: name.trim(),
      });
      if (naming === "new") onChoose(saved.id);
      setNaming(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("inbox.library.failed"));
    }
  };

  const nameForm = (
    <form onSubmit={submit} className="min-w-[9rem] px-1 py-0.5">
      <Input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (!save.isPending) setNaming(null);
        }}
        maxLength={64}
        placeholder={t("inbox.library.folder_name") ?? ""}
        aria-label={t("inbox.library.folder_name")}
        className="h-8"
      />
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </form>
  );
  const row = (value: FolderFilter, label: string, n: number, own?: LibraryFolder) => (
    <li key={value} className="group/folder flex shrink-0 items-center">
      <button
        type="button"
        onClick={() => onChoose(value)}
        aria-current={folder === value ? "true" : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center justify-between gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          folder === value
            ? "bg-accent/15 text-fg-primary"
            : "text-fg-muted hover:bg-bg-elevated/50",
        )}
      >
        <span className="truncate">{label}</span>
        <span className="text-xs tabular-nums text-fg-muted">{n}</span>
      </button>
      {own && (
        <span className="hidden shrink-0 group-focus-within/folder:flex group-hover/folder:flex">
          <button
            type="button"
            onClick={() => startNaming(own)}
            aria-label={t("inbox.library.folder_rename", { name: own.name })}
            title={t("inbox.library.folder_rename", { name: own.name })}
            className={cn(ICON_BUTTON, "p-1")}
          >
            <Pencil size={13} strokeWidth={2} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (folder === own.id) onChoose("all");
              remove.mutate(own.id);
            }}
            aria-label={t("inbox.library.folder_delete", { name: own.name })}
            title={t("inbox.library.folder_delete_hint")}
            className={cn(ICON_BUTTON, "p-1 hover:text-danger")}
          >
            <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      )}
    </li>
  );

  return (
    <nav
      aria-label={t("inbox.library.folders")}
      className="shrink-0 border-b border-border p-2 sm:w-56 sm:overflow-y-auto sm:border-b-0 sm:border-r"
    >
      <ul className="flex gap-1 overflow-x-auto sm:flex-col sm:overflow-visible">
        {row("all", t("inbox.library.all"), library.items.length)}
        {library.folders.map((f) =>
          naming !== "new" && naming?.id === f.id ? (
            <li key={f.id}>{nameForm}</li>
          ) : (
            row(f.id, f.name, count(f.id), f)
          ),
        )}
        {library.folders.length > 0 && row(NO_FOLDER, t("inbox.library.no_folder"), count(null))}
        <li className="shrink-0">
          {naming === "new" ? (
            nameForm
          ) : (
            <button
              type="button"
              onClick={() => startNaming("new")}
              className="flex w-full items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm text-accent hover:bg-bg-elevated/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <FolderPlus size={15} strokeWidth={2} aria-hidden="true" />
              {t("inbox.library.folder_add")}
            </button>
          )}
        </li>
      </ul>
    </nav>
  );
}

/* ---------------------------------------------------------------- materials */

/** What a row says under the title. */
function useSecondLine() {
  const { t } = useTranslation();
  return (item: LibraryItem): string => {
    if (item.kind === "text") return (item.text ?? "").replace(/\s+/g, " ");
    const parts = [t(`inbox.library.kind.${item.kind}`)];
    if (item.duration_seconds !== null) parts.push(formatDuration(item.duration_seconds));
    parts.push(formatFileSize(item.file_size));
    if (item.text) parts.push(item.text.replace(/\s+/g, " "));
    return parts.join(" · ");
  };
}

function Items({
  items,
  empty,
  onPick,
  onEdit,
}: {
  items: LibraryItem[];
  /** The library itself has nothing — not just this folder or this search. */
  empty: boolean;
  onPick?: (item: LibraryItem) => void;
  onEdit: (item: LibraryItem) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateLibraryItem();
  const remove = useDeleteLibraryItem();
  const secondLine = useSecondLine();
  // Deleting takes a second click on the same row.
  const [doomed, setDoomed] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <EmptyState
          icon={LibraryIcon}
          title={t(empty ? "inbox.library.empty_title" : "inbox.filtered_empty_title")}
          description={t(empty ? "inbox.library.empty" : "inbox.library.nothing_found")}
        />
      </div>
    );
  }
  return (
    <ul aria-label={t("inbox.library.title")} className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
      {items.map((item) => {
        const Icon = KIND_ICONS[item.kind];
        return (
          <li
            key={item.id}
            className="flex items-center gap-0.5 rounded-lg pr-1 hover:bg-bg-elevated/50"
          >
            <button
              type="button"
              onClick={() => (onPick ? onPick(item) : onEdit(item))}
              title={t(
                !onPick
                  ? "inbox.library.edit"
                  : item.kind === "text"
                    ? "inbox.library.pick_text"
                    : "inbox.library.pick_file",
              )}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {item.kind === "photo" ? (
                <img
                  src={libraryFileUrl(item.id)}
                  alt=""
                  loading="lazy"
                  className="h-9 w-9 shrink-0 rounded-md object-cover"
                />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-bg-elevated text-fg-muted">
                  <Icon size={17} strokeWidth={2} aria-hidden="true" />
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-fg-primary">
                  {item.title}
                </span>
                <span className="block truncate text-xs text-fg-muted">{secondLine(item)}</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => update.mutate({ id: item.id, is_quick: !item.is_quick })}
              aria-pressed={item.is_quick}
              aria-label={t("inbox.library.quick")}
              title={t(item.is_quick ? "inbox.library.quick_on" : "inbox.library.quick_off")}
              className={cn(ICON_BUTTON, item.is_quick && "text-warning hover:text-warning")}
            >
              <Star
                size={15}
                strokeWidth={2}
                aria-hidden="true"
                fill={item.is_quick ? "currentColor" : "none"}
              />
            </button>
            <button
              type="button"
              onClick={() => onEdit(item)}
              aria-label={t("inbox.library.edit")}
              title={t("inbox.library.edit")}
              className={ICON_BUTTON}
            >
              <Pencil size={15} strokeWidth={2} aria-hidden="true" />
            </button>
            {doomed === item.id ? (
              <Button
                size="sm"
                variant="danger"
                className="h-7 px-2 text-xs"
                autoFocus
                onBlur={() => setDoomed(null)}
                onClick={() => remove.mutate(item.id)}
              >
                {t("inbox.library.delete_confirm")}
              </Button>
            ) : (
              <button
                type="button"
                onClick={() => setDoomed(item.id)}
                aria-label={t("inbox.library.delete")}
                title={t("inbox.library.delete")}
                className={cn(ICON_BUTTON, "hover:text-danger")}
              >
                <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------ the form */

function ItemForm({
  item,
  library,
  folder,
  onDone,
}: {
  /** The material being changed; null for a new one. */
  item: LibraryItem | null;
  library: Library;
  /** The folder the list was showing — a new material starts in it. */
  folder: FolderFilter;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateLibraryItem();
  const update = useUpdateLibraryItem();
  const [title, setTitle] = useState(item?.title ?? "");
  const [text, setText] = useState(item?.text ?? "");
  const [inFolder, setInFolder] = useState<string>(
    item ? (item.folder_id ?? NO_FOLDER) : folder === "all" ? NO_FOLDER : folder,
  );
  const [quick, setQuick] = useState(item?.is_quick ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [asVoice, setAsVoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const isFile = item ? item.kind !== "text" : file !== null;
  const canBeVoice = file !== null && file.type.startsWith("audio/");
  const saving = create.isPending || update.isPending;
  const megabytes = library.max_file_bytes / (1024 * 1024);
  const folders = useMemo(
    () => [
      { value: NO_FOLDER, label: t("inbox.library.no_folder") },
      ...library.folders.map((f) => ({ value: f.id, label: f.name })),
    ],
    [library.folders, t],
  );

  const choose = (picked: File | undefined) => {
    if (!picked) return;
    if (picked.size > library.max_file_bytes) {
      setError(t("inbox.library.too_big", { mb: megabytes }));
      return;
    }
    setError(null);
    setFile(picked);
    setAsVoice(false);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const folderId = inFolder === NO_FOLDER ? null : inFolder;
    try {
      if (item) {
        await update.mutateAsync({
          id: item.id,
          title: title.trim() || item.title,
          text: text.trim() || null,
          folder_id: folderId,
          is_quick: quick,
        });
      } else {
        await create.mutateAsync({
          title: title.trim(),
          text: text.trim(),
          folder_id: folderId,
          is_quick: quick,
          file,
          as_voice: canBeVoice && asVoice,
        });
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("inbox.library.failed"));
    }
  };

  return (
    <form onSubmit={submit}>
      <DialogBody className="space-y-4">
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">{t("inbox.library.field_title")}</span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={128}
            placeholder={t("inbox.library.field_title_hint") ?? ""}
          />
        </label>

        {item === null && (
          <div className="space-y-1.5 text-sm">
            <span className="font-medium">{t("inbox.library.field_file")}</span>
            <input
              ref={fileInput}
              type="file"
              hidden
              onChange={(e) => {
                choose(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInput.current?.click()}
              >
                {t(file ? "inbox.library.file_change" : "inbox.library.file_choose")}
              </Button>
              {file ? (
                <>
                  <span className="min-w-0 truncate text-fg-primary">
                    {file.name} · {formatFileSize(file.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-xs text-accent hover:underline"
                  >
                    {t("inbox.library.file_remove")}
                  </button>
                </>
              ) : (
                <span className="text-xs text-fg-muted">
                  {t("inbox.library.field_file_hint", { mb: megabytes })}
                </span>
              )}
            </div>
            {canBeVoice && (
              <label className="flex items-start gap-2 pt-1">
                <Checkbox
                  checked={asVoice}
                  onCheckedChange={(v) => setAsVoice(v === true)}
                  className="mt-0.5"
                />
                <span>
                  {t("inbox.library.as_voice")}
                  <span className="block text-xs text-fg-muted">
                    {t("inbox.library.as_voice_hint")}
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {item && item.kind !== "text" && (
          <div className="space-y-1.5 text-sm">
            <span className="font-medium">{t("inbox.library.field_file")}</span>
            <p className="text-fg-muted">
              {item.file_name} · {formatFileSize(item.file_size)}
              {item.duration_seconds !== null && ` · ${formatDuration(item.duration_seconds)}`}
            </p>
            {item.kind === "photo" && (
              <img src={libraryFileUrl(item.id)} alt="" className="max-h-48 rounded-lg" />
            )}
            {(item.kind === "voice" || item.kind === "audio") && (
              <audio src={libraryFileUrl(item.id)} controls className="h-10 w-full max-w-sm" />
            )}
            {item.kind === "video" && (
              <video src={libraryFileUrl(item.id)} controls className="max-h-48 rounded-lg" />
            )}
            {item.kind === "document" && (
              <a href={libraryFileUrl(item.id)} className="text-accent hover:underline">
                {t("inbox.library.download")}
              </a>
            )}
            <p className="text-xs text-fg-muted">{t("inbox.library.file_fixed")}</p>
          </div>
        )}

        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">
            {t(isFile ? "inbox.library.field_caption" : "inbox.library.field_text")}
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={isFile ? 3 : 6}
            maxLength={isFile ? 1024 : 4096}
            className={FIELD}
          />
          <span className="block text-xs text-fg-muted">{t("inbox.library.field_text_hint")}</span>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 text-sm">
            <span className="font-medium">{t("inbox.library.field_folder")}</span>
            <SimpleSelect options={folders} value={inFolder} onValueChange={setInFolder} />
          </div>
          <label className="flex items-start gap-2 text-sm sm:pt-7">
            <Checkbox
              checked={quick}
              onCheckedChange={(v) => setQuick(v === true)}
              className="mt-0.5"
            />
            <span>
              {t("inbox.library.quick")}
              <span className="block text-xs text-fg-muted">{t("inbox.library.quick_hint")}</span>
            </span>
          </label>
        </div>

        {error && <p className="break-words text-sm text-danger">{error}</p>}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("inbox.library.cancel")}
        </Button>
        <Button
          type="submit"
          loading={saving}
          disabled={item === null && !file && !text.trim()}
        >
          {t("inbox.library.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
